import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Observable } from "rxjs";
import { debounceTime, filter, map, withLatestFrom } from "rxjs/operators";

import { Coordinates } from "@hermes/api-model-common";
import { MapBounds } from "@hermes/map-utils";
import { FindShopsParameters } from "@hermes/shop-utils";

import {
    fetchShops,
    resetShopLocator,
    setHoveredShop,
    setMode,
    setSelectedLocation,
    setShopIdsToIgnore,
    setVisibleShopIds,
    unsetHoveredShop,
} from "../actions/shop-locator.actions";
import { isCoordinates } from "../helpers/shop-locator.helper";
import {
    HoveredShopEventOrigin,
    ShopLocatorExtraInfos,
    ShopLocatorMode,
    ShopLocatorState,
    ShopsStateData,
} from "../reducers/shop-locator.state";
import {
    selectDisplayWarningMessage,
    selectExtraInfos,
    selectHasShops,
    selectHoveredShop,
    selectHoveredShopCoordinates,
    selectHoveredShopId,
    selectMode,
    selectSelectedLocation,
    selectShopIdsToIgnore,
    selectShops,
    selectShopsListHasError,
    selectShopsListLoaded,
    selectShopsWithIgnored,
    selectVisibleShopIds,
} from "../selectors/shop-locator.selectors";

@Injectable()
export class ShopLocatorFacade {
    /**
     * Describes the shops loaded in Shop Locator
     */
    public shops$ = this.store.select(selectShops);

    /**
     * Describes the shops loaded in Shop Locator by deleting the IDs to be ignored
     */
    public shopsWithIgnored$ = this.store.select(selectShopsWithIgnored);

    /**
     * Emit true when the list of shops is populated (length > 0). Else, false.
     */
    public hasShops$ = this.store.select(selectHasShops);

    /**
     * Emit true when the loading of shops has error. Else, false.
     */
    public shopsHasError$ = this.store.select(selectShopsListHasError);

    /**
     * Emit true when shops has loaded without error.
     */
    public shopsLoaded$ = this.store.select(selectShopsListLoaded);

    /**
     * Describes the coordinates of the current selected shop
     */
    public hoveredShopCoordinates$ = this.store.select(
        selectHoveredShopCoordinates,
    );

    /**
     * Describes the shop id of the current selected shop
     */
    public hoveredShopId$ = this.store.select(selectHoveredShopId);

    /**
     * Describes the shop id of the current selected shop
     */
    public hoveredShop$ = this.store.select(selectHoveredShop);

    /**
     * Describes the current selected location
     */
    public selectedLocation$ = this.store.select(selectSelectedLocation);

    /**
     * Describe in a simple Array shop IDs to be display in the shop list
     */
    public visibleShopIds$ = this.store.select(selectVisibleShopIds);

    /**
     * Describe in a simple Array shop IDs to be display in the shop list
     */
    public shopIdsToIgnore$ = this.store.select(selectShopIdsToIgnore);

    /**
     * Describe extra info about shops & the selected location (like direction URL or distance between the shop and the selected location)
     */
    public extraInfos$ = this.store.select(selectExtraInfos);

    /**
     * Describe the way the Shop Locator feature should operate.
     */
    public mode$ = this.store.select(selectMode);

    /**
     * Desribe if the warning message (if there is no result in the chosen area)
     */
    public displayWarningMessage$ = this.store.select(
        selectDisplayWarningMessage,
    );

    /**
     * Describe a new search location
     */
    public newSearch$ = this.selectedLocation$.pipe(
        filter(
            (location) =>
                isCoordinates(location) && location.address !== undefined,
        ),
    );

    /**
     * Emit on each new location and describe if the current location is a search (with Google or Baidu)
     */
    public isANewSearch$ = this.selectedLocation$.pipe(
        map(
            (location) =>
                isCoordinates(location) && location.address !== undefined,
        ),
    );

    /**
     * Describe the shops list result of the search
     */
    public shopsDisplayedAfterSearch$ = this.newSearch$.pipe(
        debounceTime(1000),
        withLatestFrom(this.visibleShopIds$, this.shopsWithIgnored$),
        filter(([, visibleShopIds]) => visibleShopIds.length > 0),
    );

    public mainStreams$: Observable<{
        shops: ShopsStateData;
        visibleShopIds: string[];
        displayWarningMessage: boolean;
        extraInfos: ShopLocatorExtraInfos;
    }> = this.visibleShopIds$.pipe(
        // Use the `withLatestFrom` operator because :
        // - `shopsWithIgnored$` is shops to be displayed in the list or map
        // - `displayWarningMessage$` is a combination of many Observables.
        //    For visual reasons, we prefer to update it at the same time as visibleShopIds$
        withLatestFrom(
            this.shopsWithIgnored$,
            this.displayWarningMessage$,
            this.extraInfos$,
        ),
        map(([visibleShopIds, shops, displayWarningMessage, extraInfos]) => ({
            shops,
            visibleShopIds,
            displayWarningMessage,
            extraInfos,
        })),
    );

    public isLoadedWithoutShops$ = combineLatest([
        this.hasShops$,
        this.shopsLoaded$,
    ]).pipe(map(([hasShops, shopsLoaded]) => shopsLoaded && !hasShops));

    constructor(private store: Store<ShopLocatorState>) {}

    public fetchShops(parameters: FindShopsParameters): void {
        this.store.dispatch(fetchShops({ parameters }));
    }

    /**
     * Remove and desactivate the current hovered shop
     */
    public unsetHoveredShop(): void {
        this.store.dispatch(unsetHoveredShop());
    }

    /**
     * Set a new hovered shop
     */
    public setHoveredShop(
        shopId: string,
        coordinates: Coordinates,
        origin: HoveredShopEventOrigin,
    ): void {
        this.store.dispatch(
            setHoveredShop({
                shopId,
                coordinates,
                origin,
            }),
        );
    }

    /**
     * Set a new selected location
     */
    public setSelectedLocation(
        address: string,
        coordinates: Coordinates,
        isCountry: boolean,
        bounds?: MapBounds,
    ): void {
        this.store.dispatch(
            setSelectedLocation({
                address,
                coordinates,
                bounds,
                isCountry,
            }),
        );
    }

    /**
     * Set a new selected location with a country.
     *
     * Note : We will use here the countryCode attribute of the Shop
     */
    public setSelectedCountry(countryCode: string): void {
        this.store.dispatch(
            setSelectedLocation({
                isCountry: true,
                countryCode,
            }),
        );
    }

    /**
     * Set a new selected location with a city.
     */
    public setSelectedCity(countryCode: string, city: string): void {
        this.store.dispatch(
            setSelectedLocation({
                isCountry: false,
                countryCode,
                city,
            }),
        );
    }

    /**
     * Set the shop id to be visible or not in the shop list
     */
    public setVisibleShopIds(visibleShopIds: string[]): void {
        this.store.dispatch(
            setVisibleShopIds({
                visibleShopIds,
                source: "map",
            }),
        );
    }

    public setShopIdsToIgnore(shopIdsToIgnore: string[]): void {
        this.store.dispatch(
            setShopIdsToIgnore({
                shopIdsToIgnore,
            }),
        );
    }

    /**
     * Set a new mode for the Shop Locator feature
     */
    public setMode(mode: ShopLocatorMode): void {
        this.store.dispatch(
            setMode({
                mode,
            }),
        );
    }

    /**
     * Reset the Shop Locator data - except for the Shops list.
     */
    public resetShopLocator(): void {
        this.store.dispatch(resetShopLocator());
    }
}
